/**
 * Street races: night-time point-to-point runs through Low Borough, organised on the Basin Crane Quay.
 * Four courses (tiers; the fourth runs round Kiln Common); win one to open the next. Everyone on the grid puts a stake on the bonnet and the
 * winner takes the pot. Original names and places throughout.
 *
 * Stakes are kept modest on purpose: a race is over in about a minute and the courses rotate once all three
 * are open, so a rotation of the first three nets £1,700 — a couple of street jobs' worth, at the risk of the stake and the
 * heat racing brings. Bigger pots would out-earn the story jobs.
 */
import type { RouteSpec } from './roadLine';

export interface RivalDef {
  name: string;
  /** Vehicle spec id. */
  spec: string;
  paint: number;
}

export interface RaceTier {
  tier: number;
  title: string;
  /** Short name of the course for the book ("Quayside Sprint"). */
  course: string;
  route: RouteSpec;
  /** Where the grid is, in words ("Dock Road, by the lock"). */
  gridAt: string;
  stake: number;
  rivals: RivalDef[];
  /** Rivals' cruising speed on the straights (m/s) and how much faster than the autopilot's corner speeds they take bends. */
  cruise: number;
  corner: number;
  /** The organiser's line at the start. */
  intro: string;
}

export const RACE_GIVER = 'docks-crane-quay';
export const RACE_ORGANISER = 'Rita Vance';

const VINCE: RivalDef = { name: 'Vince Lally', spec: 'coupe', paint: 0xb8382a };
const KENNY: RivalDef = { name: 'Kenny Stoat', spec: 'compact', paint: 0xd8b43a };
const RAY: RivalDef = { name: 'Ray Bellamy', spec: 'saloon', paint: 0x2c2c30 };
const SMUDGE: RivalDef = { name: 'Smudge', spec: 'coupe', paint: 0x1f5a3a };
const DOLLY: RivalDef = { name: 'Dolly Prentice', spec: 'coupe', paint: 0xe0dcd0 };

export const RACE_TIERS_DEF: readonly RaceTier[] = [
  {
    tier: 1,
    title: 'Quayside Sprint',
    course: 'the Quayside Sprint',
    route: { nodes: ['lk_d', 'qs_d', 'qs_bo', 'qs_r', 'pa_r', 'cl_r', 'cl_d', 'pa_d'], startT: 0.25, endT: 0.6 },
    gridAt: 'Dock Road, by Lock Bridge',
    stake: 100,
    rivals: [KENNY, RAY],
    cruise: 20,
    corner: 1,
    intro: 'Round the quay, along the Ropewalk, back up Cinder Lane and home along Dock Road. Mind the bollards.',
  },
  {
    tier: 2,
    title: 'Cinder Circuit',
    course: 'the Cinder Circuit',
    route: { nodes: ['gw_c', 'ar_c', 'cl_c', 'cl_b', 'w_b', 'w_c', 'w_w', 'cl_w', 'cl_m', 'as_m', 'gw_c'], startT: 0.3, endT: 0.7 },
    gridAt: 'Coalgate, by the Gasworks Lane corner',
    stake: 200,
    rivals: [VINCE, KENNY, RAY],
    cruise: 25,
    corner: 1.1,
    intro: 'Out west, down Slag Road, along the wharf and back under the arches. Arch Lane’s one car wide. Remember that.',
  },
  {
    tier: 3,
    title: 'The Borough Run',
    course: 'the Borough Run',
    route: { nodes: ['cl_n', 'ar_n', 'gw_n', 'pa_n', 'bl_n', 'tr_n', 'tr_b', 'tr_c', 'sq_nw', 'sq_sw', 'tr_w', 'lk_w', 'lk_d', 'qs_d', 'pa_d'], startT: 0.4, endT: 0.5 },
    gridAt: 'Kessmouth Road, Cinder Yards end',
    stake: 300,
    rivals: [VINCE, SMUDGE, RAY],
    cruise: 29,
    corner: 1.2,
    intro: 'Kessmouth Road flat out, down Tanner’s Row, past the square, over Lock Bridge and along the docks. Biggest pot on the river.',
  },
  {
    // Kiln Common: the long loop — Kiln Road east, up Hob Lane, back along Lamb Walk's setts, down Coal
    // Drop Lane, along Kessmouth Road and home by Brickfield Lane. ~1.2 km, the borough's longest course.
    tier: 4,
    title: 'The Kiln Loop',
    course: 'the Kiln Loop',
    route: { nodes: ['k_w', 'k_ar', 'k_cd', 'k_ks', 'k_es', 'k_hl', 'lw_hl', 'lw_es', 'lw_ks', 'lw_cd', 'gw_n', 'ar_n', 'cl_n', 'w_n', 'k_w'], startT: 0.3, endT: 0.6 },
    gridAt: 'Kiln Road, Brickfield end',
    stake: 350,
    rivals: [VINCE, SMUDGE, DOLLY],
    cruise: 30,
    corner: 1.2,
    intro: 'All the way round Kiln Common: Kiln Road, Hob Lane, the cobbles on Lamb Walk, Coal Drop Lane and back. Dolly’s never lost it.',
  },
];

export function raceTier(tier: number): RaceTier {
  return RACE_TIERS_DEF[Math.max(0, Math.min(RACE_TIERS_DEF.length - 1, tier - 1))]!;
}
